import SelectPeriod from '@/components/ui/select-period'
import { Period } from '@/shared/types/dashboard'
import {
  RefreshCw,
  Clock,
  MessageCircle,
  ChevronDown,
  Share2,
  Copy,
  X,
  ArrowLeft,
} from 'lucide-react'
import { formatDateTime } from '@/core/shared/utils/timeAgo'
import { Button } from '@/presentation/components/ui/button'
import { configService } from '@/core/services/ConfigService'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/presentation/components/ui/collapsible'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/presentation/components/ui/dialog'
import { getPeriodLabel } from '@/core/shared'
import { useAuth } from '@/presentation/hooks/useAuth'
import { useState } from 'react'
import { toast } from 'sonner'

interface HeaderProps {
  title: string
  subtitle: string
  selectedPeriod: Period
  onPeriodChange: (period: Period, customDates?: { startDate: string; endDate: string }) => void
  onRefresh?: () => void
  lastUpdatedAt?: string
  isRefreshing?: boolean
  onBackClick?: () => void
  showShareButton?: boolean
}

export default function Header({
  title,
  subtitle,
  selectedPeriod,
  onPeriodChange,
  onRefresh,
  lastUpdatedAt,
  isRefreshing = false,
  onBackClick,
  showShareButton = false,
}: HeaderProps) {
  const mediatorConfig = configService.mediator
  const [isOpen, setIsOpen] = useState(false)
  const preRegistrationConfig = configService.preRegistration
  const { user } = useAuth()
  const [isSharing, setIsSharing] = useState(false)
  const [showCopyDialog, setShowCopyDialog] = useState(false)
  const [shareUrl, setShareUrl] = useState('')

  const handleMediatorContact = () => {
    const { number, message } = mediatorConfig
    if (!number) return

    const url = `https://wa.me/${number}${message ? `?text=${encodeURIComponent(message)}` : ''}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleCopyFromDialog = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl)
        toast.success('Link copiado com sucesso!')
        setShowCopyDialog(false)
      } else {
        toast.error('Não foi possível copiar automaticamente')
      }
    } catch {
      toast.error('Erro ao copiar o link')
    }
  }

  const handleSharePreRegistration = async () => {
    if (!preRegistrationConfig.url || !user?.id) return

    const url = `${preRegistrationConfig.url}?uid=${user.id}`
    setShareUrl(url)

    setIsSharing(true)
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url)
        toast.success('Link copiado para a área de transferência!')
      } else {
        setShowCopyDialog(true)
      }
    } catch {
      toast.error('Erro ao copiar o link')
      setShowCopyDialog(true)
    } finally {
      setIsSharing(false)
    }
  }

  return (
    <header className="bg-foreground border-b border-background px-6 py-4">
      <div className="flex items-start flex-col gap-4">
        {/* Mobile Layout */}
        <div className="block sm:hidden w-full">
          <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  {onBackClick && (
                    <button
                      onClick={onBackClick}
                      className="flex items-center justify-center p-2 text-text-title hover:bg-background rounded-md transition-colors flex-shrink-0 mb-2"
                      title="Voltar"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                  )}
                  <h1 className="text-2xl font-bold text-text-title">
                    {title}
                  </h1>
                  <p className="text-text-subtitle">{subtitle}</p>
                  {!isOpen && (
                    <p className="text-base text-text-subtitle mt-1">
                      Período Selecionado:{' '}
                      <span className="font-medium text-text-title">
                        {getPeriodLabel(selectedPeriod)}
                      </span>
                    </p>
                  )}
                  {lastUpdatedAt && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <Clock className="h-3 w-3 text-text-subtitle" />
                      <p className="text-xs text-text-subtitle">
                        Última atualização: {formatDateTime(lastUpdatedAt)}
                      </p>
                    </div>
                  )}
                </div>
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-2 text-text-title hover:bg-muted"
                  >
                    <ChevronDown
                      className={`h-5 w-5 transition-transform duration-200 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </Button>
                </CollapsibleTrigger>
              </div>

              <CollapsibleContent className="space-y-4">
                <div className="flex flex-col gap-2">
                  {showShareButton && preRegistrationConfig.url && (
                    <Button
                      onClick={handleSharePreRegistration}
                      variant="default"
                      size="sm"
                      disabled={isSharing}
                      className="bg-greenGarupa hover:bg-greenGarupa/80 text-sm font-medium rounded-md transition-colors w-full text-white"
                      title="Compartilhar link de pré-cadastro"
                    >
                      <Share2 className="h-4 w-4 mr-2" />
                      Copiar Pré-Cadastro
                    </Button>
                  )}
                  {mediatorConfig.number && (
                    <Button
                      onClick={handleMediatorContact}
                      variant="default"
                      size="sm"
                      className="bg-pinkGarupa hover:bg-pinkGarupa/80 text-sm font-medium rounded-md transition-colors w-full text-white"
                      title="Entrar em contato com o mediador"
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Falar com Mediador
                    </Button>
                  )}
                  {onRefresh && (
                    <Button
                      onClick={onRefresh}
                      disabled={isRefreshing}
                      variant="default"
                      size="sm"
                      className="bg-background hover:bg-muted text-text-title text-sm font-medium rounded-md transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Atualizar dados"
                    >
                      <RefreshCw
                        className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`}
                      />
                      {isRefreshing ? 'Atualizando...' : 'Atualizar'}
                    </Button>
                  )}
                </div>
                <SelectPeriod
                  selectedPeriod={selectedPeriod}
                  onPeriodChange={onPeriodChange}
                />
              </CollapsibleContent>
            </div>
          </Collapsible>
        </div>

        {/* Desktop Layout */}
        <div className="hidden sm:flex flex-col gap-4 w-full">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 w-full">
            <div>
              {onBackClick && (
                <button
                  onClick={onBackClick}
                  className="flex items-center justify-center p-2 text-text-title hover:bg-background rounded-md transition-colors flex-shrink-0 mb-2"
                  title="Voltar"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
              )}
              <h1 className="text-2xl font-bold text-text-title">{title}</h1>
              <p className="text-text-subtitle">{subtitle}</p>
              {lastUpdatedAt && (
                <div className="flex items-center gap-1.5 mt-1">
                  <Clock className="h-3 w-3 text-text-subtitle" />
                  <p className="text-xs text-text-subtitle">
                    Última atualização: {formatDateTime(lastUpdatedAt)}
                  </p>
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              {showShareButton && preRegistrationConfig.url && (
                <Button
                  onClick={handleSharePreRegistration}
                  variant="default"
                  size="sm"
                  disabled={isSharing}
                  className="bg-greenGarupa hover:bg-greenGarupa/80 text-sm font-medium rounded-md transition-colors w-full sm:w-auto text-white"
                  title="Compartilhar link de pré-cadastro"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Compartilhar Pré-Cadastro
                </Button>
              )}
              {mediatorConfig.number && (
                <Button
                  onClick={handleMediatorContact}
                  variant="default"
                  size="sm"
                  className="bg-pinkGarupa hover:bg-pinkGarupa/80 text-sm font-medium rounded-md transition-colors w-full sm:w-auto sm:flex-shrink-700 text-white"
                  title="Entrar em contato com o mediador"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Falar com Mediador
                </Button>
              )}
              {onRefresh && (
                <Button
                  onClick={onRefresh}
                  disabled={isRefreshing}
                  variant="default"
                  size="sm"
                  className="bg-background hover:bg-muted text-text-title text-sm font-medium rounded-md transition-colors w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Atualizar dados"
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`}
                  />
                  {isRefreshing ? 'Atualizando...' : 'Atualizar'}
                </Button>
              )}
            </div>
          </div>
          <SelectPeriod
            selectedPeriod={selectedPeriod}
            onPeriodChange={onPeriodChange}
          />
        </div>
      </div>

      <Dialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
        <DialogContent className="sm:max-w-md bg-foreground border-background max-w-[calc(100%-2rem)] p-6 rounded-md">
          <button
            onClick={() => setShowCopyDialog(false)}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
          >
            <X className="h-4 w-4 text-text-title dark:text-white" />
            <span className="sr-only">Fechar</span>
          </button>
          <DialogHeader>
            <DialogTitle className="text-text-title">
              Copiar Link de Pré-Cadastro
            </DialogTitle>
            <DialogDescription className="text-text-subtitle">
              Copie o link abaixo para compartilhar o formulário de pré-cadastro
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center space-x-2">
            <div className="grid flex-1 gap-2">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="w-full rounded-md border border-border bg-background text-text-title dark:text-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>
            <Button
              type="button"
              size="sm"
              onClick={handleCopyFromDialog}
              className="px-3 bg-greenGarupa hover:bg-greenGarupa/80 text-white"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <div className="text-xs text-text-muted mt-2">
            Clique no ícone de copiar ou selecione o texto acima
          </div>
        </DialogContent>
      </Dialog>
    </header>
  )
}
